"use client";

import { useCallback, useRef } from "react";
import type { AuthenticationResult } from "@azure/msal-browser";

import { logError, logWarning } from "@/services/DatadogService";
import { getMicrosoftPublicClient } from "@/utils/SSOConfig";

export interface MicrosoftAuthOptions {
  onSuccess?: (idToken: string) => void;
  onError?: (error: unknown) => void;
}

const FOCUS_GRACE_PERIOD_MS = 2000;

export const useMicrosoftAuth = (options?: MicrosoftAuthOptions) => {
  const optionsRef = useRef(options);
  const isProcessingRef = useRef(false);

  optionsRef.current = options;

  const signIn = useCallback(async (): Promise<string | null> => {
    if (typeof window === "undefined") return null;

    let resolved = false;
    let focusCleanup: (() => void) | null = null;

    const handleResolved = () => {
      resolved = true;
      focusCleanup?.();
    };

    try {
      isProcessingRef.current = false;

      const msalInstance = await getMicrosoftPublicClient();

      const loginPromise = msalInstance.loginPopup({
        scopes: ["openid", "profile", "email"],
        overrideInteractionInProgress: true,
      });

      const focusCancelledPromise = new Promise<"cancelled">((resolve) => {
        let focusTimer: ReturnType<typeof setTimeout> | null = null;

        const handleFocus = () => {
          if (resolved) return;
          focusTimer = setTimeout(() => {
            if (!resolved) {
              resolve("cancelled");
            }
          }, FOCUS_GRACE_PERIOD_MS);
        };

        window.addEventListener("focus", handleFocus);

        focusCleanup = () => {
          window.removeEventListener("focus", handleFocus);
          if (focusTimer) clearTimeout(focusTimer);
        };
      });

      const result = await Promise.race([loginPromise, focusCancelledPromise]);

      if (result === "cancelled") {
        handleResolved();
        logWarning("Microsoft Sign In popup was likely closed by user", {
          component: "useMicrosoftAuth",
          action: "signIn",
          reason: "focus_timeout",
        });
        optionsRef.current?.onError?.(new Error("user_cancelled"));
        return null;
      }

      handleResolved();

      const response = result as AuthenticationResult;
      const idToken = response.idToken;
      if (!idToken) {
        const error = new Error("Microsoft Sign In did not return an id_token");
        logError(error, {
          context: "MicrosoftAuthHook",
          action: "signIn",
        });
        optionsRef.current?.onError?.(error);
        return null;
      }

      if (!isProcessingRef.current) {
        isProcessingRef.current = true;
        optionsRef.current?.onSuccess?.(idToken);
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 1000);
      }
      return idToken;
    } catch (error) {
      handleResolved();
      if (!isProcessingRef.current) {
        isProcessingRef.current = true;
        logWarning("Microsoft Sign In was cancelled or failed", {
          error: error instanceof Error ? error.message : error,
          component: "useMicrosoftAuth",
          action: "signIn",
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        optionsRef.current?.onError?.(error);
        setTimeout(() => {
          isProcessingRef.current = false;
        }, 1000);
      }
      return null;
    }
  }, []);

  return { signIn };
};
